/**
 * Automation presets shown in the "Type" picker of the create dialog. Each is a
 * usage example that pre-fills the prompt with a ready-made instruction; the
 * user can edit it afterwards. Almost every preset maps to the `agent-chat`
 * capability under the hood — the picker chooses a PROMPT TEMPLATE, not a scheduler
 * capability (the raw `insights` capability is never created from the UI; the
 * agent does insights work from the prompt and delivers into the chat).
 * `id` is stable/code-facing; `label` + `seedPrompt` are user-facing. UI copy is
 * English-only.
 */
export interface AutomationPreset {
  id: string;
  label: string;
  seedPrompt: string;
}

// Declared standalone (not AUTOMATION_PRESETS[0]) so DEFAULT_PRESET has a concrete, non-undefined
// type under noUncheckedIndexedAccess.
const LINKEDIN_PRESET: AutomationPreset = {
  id: 'linkedin',
  label: 'Watch LinkedIn',
  seedPrompt:
    'Track new LinkedIn activity for the key accounts and people we follow. ' +
    'Collect new posts, role changes, and notable updates since the last run, ' +
    'highlight what matters for our business, and send a short digest with ' +
    'links to the sources.',
};

export const AUTOMATION_PRESETS: ReadonlyArray<AutomationPreset> = [
  LINKEDIN_PRESET,
  {
    id: 'market',
    label: 'Market analysis',
    seedPrompt:
      'Give a market overview for our industry over the period: key competitor ' +
      'moves, deals, product launches, and trends. Highlight what could affect ' +
      'us and give brief takeaways with links to the sources.',
  },
  {
    id: 'blog',
    label: 'Critical blog updates',
    seedPrompt:
      'Check the blogs and release notes of key vendors and competitors for ' +
      'critical updates (new features, pricing changes, deprecations). Send only ' +
      'the significant changes since the last run, with a short impact assessment ' +
      'for us.',
  },
  {
    id: 'competitors',
    label: 'Competitor digest',
    seedPrompt:
      'Compile a digest of news about our main competitors over the period: ' +
      'announcements, hiring, press mentions, and notable changes. Group by ' +
      'company and highlight what needs our attention.',
  },
  // Free-form type: no template binding — the user writes their own prompt + schedule
  // (e.g. "send me a random recipe every day at 12:00"). Still an agent-chat automation.
  {
    id: 'custom',
    label: 'Custom',
    seedPrompt: '',
  },
];

export const DEFAULT_PRESET = LINKEDIN_PRESET;

/** The free-form preset id; the dialog skips the LinkedIn link editor and seeds an empty prompt. */
export const CUSTOM_PRESET_ID = 'custom';

/** The "Watch LinkedIn" preset id; the dialog swaps in the multi-link attach editor. */
export const LINKEDIN_PRESET_ID = LINKEDIN_PRESET.id;

const PRESET_LABELS = new Set(AUTOMATION_PRESETS.map((p) => p.label));

/** True when `name` is still one of the preset labels (i.e. not user-customized). */
export function isPresetLabel(name: string): boolean {
  return PRESET_LABELS.has(name.trim());
}

export function presetById(id: string): AutomationPreset | undefined {
  return AUTOMATION_PRESETS.find((p) => p.id === id);
}
